import {
	Anchor,
	HierarchyTable,
	Label,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	type Hierarchy,
} from "@exopengithub/exokit";
import { Link } from "react-router";
import { Button } from "@exopengithub/exokit/v2";
import { Select } from "molecules/select/Select";
import { UnorganizedCompaniesAlert } from "molecules/unorganized-companies-alert/UnorganizedCompaniesAlert";
import { DraggableCompanyList } from "molecules/unorganized-companies-alert/DraggableCompanyList";
import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { useTranslation } from "react-i18next";
import {
	IconArrowsRightLeft,
	IconCirclePlus,
	IconTrash,
} from "@tabler/icons-react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
	useRemoveCompanyHierarchyMutation,
	useUpdateCompanyHierarchyMutation,
} from "../../../common/service/nexus/nexusApi.js";
import {
	Card,
	CardHeader,
	CardItemsHeader,
} from "../../planning/components/Card";

import { useId, useState } from "react";
import { AddCompanyToHierarchyModal } from "./AddCompanyToHierarchyModal";
import { isDefined } from "../../../common/utils/filters/isDefined.js";
import { RemoveCompanyFromHierarchyModal } from "./RemoveCompanyFromHierarchyModal.js";
import { MoveCompanyToParentModal } from "./MoveCompanyToParentModal.js";

import type {
	LegalEntityHierarchy,
	UpdateHierarchyRequestBody,
	LegalEntity,
} from "src/common/service/nexus/endpoints/companyDomain/types.js";

import {
	useLoadedCompanyDomain,
	useLoadedCompanyHierarchy,
	useLoadedLegalEntities,
} from "src/routes/routeDataHooks.js";
import { DOMAIN_TERMS } from "src/common/domainTerms.js";
import { useUser } from "src/common/context/userContextUtils";
import { COMPANYADMIN } from "src/common/service/users.ts";

const convertHierarchyToUpdateRequest = (
	hierarchy: LegalEntityHierarchy,
): UpdateHierarchyRequestBody => ({
	legalEntityId: hierarchy.legalEntity.id,
	children: hierarchy.children.map(convertHierarchyToUpdateRequest),
});

// asLinks is false in edit mode: rows are draggable there, and a clickable
// label competing with a drag handle for the same click is a bad idea.
//
// permittedIds is every id in the caller's own legal-entity list. A group's
// shape is not considered confidential within a domain — only the data
// behind each company is — so an unpermitted node is shown but greyed out,
// not hidden. GET /hierarchy is scoped to the same permission set today, so
// this check is currently inert; it starts excluding nodes once that
// endpoint returns the domain's full tree regardless of permissions. If
// permittedIds is ever dropped, an unpermitted company renders as an
// ordinary link that 403s on click — CI won't catch that until the
// endpoint's behavior actually changes, hence the shrink-legalEntities test
// below.
const convertCompanyHierarchyToHierarchy = (
	companyHierarchy: LegalEntityHierarchy,
	companyDomainId: string,
	permittedIds: ReadonlySet<string>,
	asLinks: boolean,
): Hierarchy => {
	const { id, name } = companyHierarchy.legalEntity;
	const isPermitted = permittedIds.has(id);
	return {
		id,
		label:
			asLinks && isPermitted ? (
				<Anchor
					to={`/company-domains/${companyDomainId}/legal-entities/${id}/info`}
					component={Link}
				>
					{name}
				</Anchor>
			) : isPermitted ? (
				name
			) : (
				<span className="text-gray-400">{name}</span>
			),
		children: companyHierarchy.children.map((child) =>
			convertCompanyHierarchyToHierarchy(
				child,
				companyDomainId,
				permittedIds,
				asLinks,
			),
		),
	};
};

export const addLegalEntityToCompanyHierarchy = (
	parentId: string,
	child: LegalEntity,
	companyHierarchy: LegalEntityHierarchy,
): LegalEntityHierarchy => {
	if (parentId === companyHierarchy.legalEntity.id) {
		return {
			legalEntity: companyHierarchy.legalEntity,
			children: [
				...companyHierarchy.children,
				{
					legalEntity: child,
					children: [],
				},
			],
		};
	}

	return {
		...companyHierarchy,
		children: companyHierarchy.children.map((hierarchy) =>
			addLegalEntityToCompanyHierarchy(parentId, child, hierarchy),
		),
	};
};

// Unlike addLegalEntityToCompanyHierarchy, preserves the subtree's own children — moves a branch, not just a leaf.
export const insertSubtreeIntoCompanyHierarchy = (
	parentId: string,
	subtree: LegalEntityHierarchy,
	companyHierarchy: LegalEntityHierarchy,
): LegalEntityHierarchy => {
	if (parentId === companyHierarchy.legalEntity.id) {
		return {
			...companyHierarchy,
			children: [...companyHierarchy.children, subtree],
		};
	}

	return {
		...companyHierarchy,
		children: companyHierarchy.children.map((child) =>
			insertSubtreeIntoCompanyHierarchy(parentId, subtree, child),
		),
	};
};

export const removeLegalEntityFromCompanyHierarchy = (
	id: string,
	companyHierarchy: LegalEntityHierarchy,
): LegalEntityHierarchy | null => {
	if (id === companyHierarchy.legalEntity.id) {
		return null;
	}

	return {
		...companyHierarchy,
		children: companyHierarchy.children
			.map((child) => removeLegalEntityFromCompanyHierarchy(id, child))
			.filter(isDefined),
	};
};

export const extractLegalEntityIdsFromHierarchy = (
	hierarchy: LegalEntityHierarchy | null,
): string[] => {
	if (!hierarchy) {
		return [];
	}

	const ids = [hierarchy.legalEntity.id];
	for (const child of hierarchy.children) {
		ids.push(...extractLegalEntityIdsFromHierarchy(child));
	}
	return ids;
};

export const findLegalEntityHierarchy = (
	id: string,
	companyHierarchy: LegalEntityHierarchy,
): LegalEntityHierarchy | null => {
	if (id === companyHierarchy.legalEntity.id) {
		return companyHierarchy;
	}
	for (const child of companyHierarchy.children) {
		const found = findLegalEntityHierarchy(id, child);
		if (found) {
			return found;
		}
	}
	return null;
};

export const flattenLegalEntitiesInHierarchy = (
	companyHierarchy: LegalEntityHierarchy,
): LegalEntity[] => [
	companyHierarchy.legalEntity,
	...companyHierarchy.children.flatMap(flattenLegalEntitiesInHierarchy),
];

export const findParentOfChild = (
	childId: string,
	companyHierarchy: LegalEntityHierarchy,
): LegalEntityHierarchy | null => {
	if (
		companyHierarchy.children.some((child) => child.legalEntity.id === childId)
	) {
		return companyHierarchy;
	}
	for (const child of companyHierarchy.children) {
		const found = findParentOfChild(childId, child);
		if (found) {
			return found;
		}
	}
	return null;
};

// Whether nodeId can move under parentId: not onto itself, not onto the
// root, not into its own subtree (which would either orphan it or nest the
// tree inside itself). The only enforcement point that actually runs for
// every drop path — HierarchyTable's "insert as sibling" reorder drop zone
// hardcodes its own canBeDropped to always-true and calls onReorder instead,
// bypassing whatever canBeDropped the caller passed in.
export const canMoveWithinHierarchy = (
	nodeId: string,
	parentId: string,
	rootHierarchy: LegalEntityHierarchy,
): boolean => {
	if (nodeId === parentId) {
		return false;
	}
	if (nodeId === rootHierarchy.legalEntity.id) {
		return false;
	}
	// A stale parentId (e.g. the Move modal stayed open while another update
	// removed it) must be rejected here — insertSubtreeIntoCompanyHierarchy
	// finds no match for an id that isn't in the tree and silently no-ops the
	// insertion, which would unplace the branch instead of moving it.
	if (!findLegalEntityHierarchy(parentId, rootHierarchy)) {
		return false;
	}
	const existingSubtree = findLegalEntityHierarchy(nodeId, rootHierarchy);
	return !extractLegalEntityIdsFromHierarchy(existingSubtree).includes(
		parentId,
	);
};

export const GroupHierarchyCard = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const legalEntities = useLoadedLegalEntities();
	const hierarchy = useLoadedCompanyHierarchy();
	const permittedLegalEntityIds = new Set(
		legalEntities.map((legalEntity) => legalEntity.id),
	);

	const [updateHierarchy] = useUpdateCompanyHierarchyMutation();
	const [removeHierarchy] = useRemoveCompanyHierarchyMutation();

	const hasCompanyAdmin = useUser().hasPermissions(companyDomain.id, [
		COMPANYADMIN,
	]);
	// With 0-1 companies in the domain there's nothing to organize — the
	// "which company is the parent" picker and the "N unplaced" banner are
	// both meaningless prompts with no real choice behind them.
	const hasMultipleCompanies = legalEntities.length >= 2;

	const [modalState, setModalState] = useState<{
		legalEntity: LegalEntity;
		variant: "REMOVING" | "ADDING" | "MOVING";
	} | null>(null);
	// Raw toggle intent; derived below so a hierarchy deleted-then-recreated
	// can't land straight back in edit mode without a useEffect state-sync.
	const [isEditingStructureToggle, setIsEditingStructureToggle] =
		useState(false);
	const isEditingStructure =
		isEditingStructureToggle && hierarchy.hierarchy != null && hasCompanyAdmin;
	const parentCompanyLabelId = useId();
	// The full tree is PUT on every mutation, so two in-flight writes would
	// race and the loser's edit is silently lost — serialize them.
	const [isMutating, setIsMutating] = useState(false);
	// Placing/moving a company in a large tree can land it far from where the
	// drag started, out of view — highlightedNodeId expands the path to it and
	// scrolls it into view, so the user can see where it landed.
	const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(
		null,
	);

	const legalEntitiesInHierarchy = extractLegalEntityIdsFromHierarchy(
		hierarchy.hierarchy,
	);

	const availableLegalEntities = legalEntities.filter(
		(legalEntity) => !legalEntitiesInHierarchy.includes(legalEntity.id),
	);

	const canBeDropped = (node: Hierarchy, parentNode: Hierarchy | null) =>
		!isMutating &&
		parentNode != null &&
		hierarchy.hierarchy != null &&
		canMoveWithinHierarchy(node.id, parentNode.id, hierarchy.hierarchy);

	// Dragging a branch moves the whole branch, not just the node — shared by
	// both onDrop (dropped directly on a row) and onReorder (HierarchyTable's
	// own "insert as sibling" drop zone, which fires instead of onDrop when
	// the dragged node and the target already share a parent).
	// Returns whether the move actually persisted, so a caller like the Move
	// modal knows not to close on a guard rejection or a failed PUT — both
	// cases already report their own error (or intentionally report none, for
	// a silent guard rejection), so callers should not also throw/notify.
	const moveIntoHierarchy = async (
		nodeId: string,
		parentId: string,
	): Promise<boolean> => {
		if (
			!hierarchy.hierarchy ||
			!canMoveWithinHierarchy(nodeId, parentId, hierarchy.hierarchy)
		) {
			return false;
		}
		const existingSubtree = findLegalEntityHierarchy(
			nodeId,
			hierarchy.hierarchy,
		);
		const legalEntity =
			existingSubtree?.legalEntity ??
			legalEntities.find((entity) => entity.id === nodeId);
		if (!legalEntity) {
			return false;
		}
		const subtree: LegalEntityHierarchy = existingSubtree ?? {
			legalEntity,
			children: [],
		};
		const withoutNode =
			removeLegalEntityFromCompanyHierarchy(nodeId, hierarchy.hierarchy) ??
			hierarchy.hierarchy;
		const newHierarchy = insertSubtreeIntoCompanyHierarchy(
			parentId,
			subtree,
			withoutNode,
		);
		setIsMutating(true);
		try {
			await updateHierarchy({
				companyDomainId: companyDomain.id,
				hierarchy: convertHierarchyToUpdateRequest(newHierarchy),
			}).unwrap();
			setHighlightedNodeId(nodeId);
			return true;
		} catch {
			showErrorNotification({ message: t("Error") });
			return false;
		} finally {
			setIsMutating(false);
		}
	};

	const onDropIntoHierarchy = (
		node: Hierarchy,
		parentNode: Hierarchy | null,
	) => {
		if (!parentNode) {
			return;
		}
		void moveIntoHierarchy(node.id, parentNode.id);
	};

	const onReorderWithinHierarchy = (
		node: Hierarchy,
		between: [Hierarchy | null, Hierarchy | null],
	) => {
		const sibling = between[1] ?? between[0];
		if (!sibling || !hierarchy.hierarchy) {
			return;
		}
		const parent = findParentOfChild(sibling.id, hierarchy.hierarchy);
		if (!parent) {
			return;
		}
		void moveIntoHierarchy(node.id, parent.legalEntity.id);
	};

	const canDropToUnsorted = (item: { id: string }) =>
		!isMutating &&
		hierarchy.hierarchy != null &&
		item.id !== hierarchy.hierarchy.legalEntity.id &&
		findLegalEntityHierarchy(item.id, hierarchy.hierarchy) != null;

	// Same confirmation as the trash-icon flow (RemoveCompanyFromHierarchyModal)
	// — dragging a branch out is exactly as destructive as clicking delete on
	// it, so it gets the same "are you sure" rather than an unconfirmed drop.
	const onDropToUnsorted = (item: { id: string }) => {
		const legalEntity = legalEntities.find((entity) => entity.id === item.id);
		if (!legalEntity) {
			return;
		}
		setModalState({ legalEntity, variant: "REMOVING" });
	};

	const removeFromHierarchy = async (legalEntity: LegalEntity) => {
		if (!hierarchy.hierarchy) {
			return;
		}
		const newHierarchy = removeLegalEntityFromCompanyHierarchy(
			legalEntity.id,
			hierarchy.hierarchy,
		);
		setIsMutating(true);
		try {
			if (!newHierarchy) {
				await removeHierarchy({
					companyDomainId: companyDomain.id,
				}).unwrap();
				return;
			}
			await updateHierarchy({
				companyDomainId: companyDomain.id,
				hierarchy: convertHierarchyToUpdateRequest(newHierarchy),
			}).unwrap();
		} catch {
			showErrorNotification({ message: t("Error") });
		} finally {
			setIsMutating(false);
		}
	};

	const closeModal = () => setModalState(null);

	const renderRowActions = (node: Hierarchy, _parent: Hierarchy | null) => {
		if (!hasCompanyAdmin) {
			return null;
		}
		const matchingLegalEntity = legalEntities.find(
			(legalEntity) => legalEntity.id === node.id,
		);
		if (!matchingLegalEntity) {
			return null;
		}
		// The root can never have a valid parent (canMoveWithinHierarchy always
		// rejects it as a move source), so a Move action for it would only open
		// a dialog with no eligible destination.
		const isRoot = hierarchy.hierarchy?.legalEntity.id === node.id;
		return (
			<>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							aria-label={t("Add subsidiary")}
							variant="ghost"
							disabled={isMutating || availableLegalEntities.length === 0}
							onClick={() =>
								setModalState({
									legalEntity: matchingLegalEntity,
									variant: "ADDING",
								})
							}
							icon={<IconCirclePlus />}
						/>
					</TooltipTrigger>
					<TooltipContent>{t("Add subsidiary")}</TooltipContent>
				</Tooltip>
				{!isRoot && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								aria-label={t("Move company")}
								variant="ghost"
								disabled={isMutating}
								onClick={() =>
									setModalState({
										legalEntity: matchingLegalEntity,
										variant: "MOVING",
									})
								}
								icon={<IconArrowsRightLeft />}
							/>
						</TooltipTrigger>
						<TooltipContent>{t("Move company")}</TooltipContent>
					</Tooltip>
				)}
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							aria-label={t("Delete legal entity")}
							variant="ghost"
							disabled={isMutating}
							onClick={() =>
								setModalState({
									legalEntity: matchingLegalEntity,
									variant: "REMOVING",
								})
							}
							icon={<IconTrash />}
						/>
					</TooltipTrigger>
					<TooltipContent>{t("Delete legal entity")}</TooltipContent>
				</Tooltip>
			</>
		);
	};

	return (
		<Card className="mt-4">
			<CardHeader className="items-center">
				<CardItemsHeader
					heading={t(DOMAIN_TERMS.GROUP_STRUCTURE)}
					subHeading={t("Specify the legal entities and their relationships")}
				/>
				{hierarchy.hierarchy && hasCompanyAdmin && (
					<Button
						variant="secondaryGray"
						disabled={isMutating}
						onClick={() => setIsEditingStructureToggle((editing) => !editing)}
					>
						{isEditingStructure ? t("Done") : t("Edit structure")}
					</Button>
				)}
			</CardHeader>
			{!isEditingStructure && hasMultipleCompanies && (
				<UnorganizedCompaniesAlert companies={availableLegalEntities} />
			)}
			{hierarchy.hierarchy ? (
				<>
					<AddCompanyToHierarchyModal
						isOpen={modalState?.variant === "ADDING"}
						availableLegalEntities={availableLegalEntities}
						onAdd={async (legalEntity) => {
							if (modalState && hierarchy.hierarchy) {
								const newHierarchy = addLegalEntityToCompanyHierarchy(
									modalState.legalEntity.id,
									legalEntity,
									hierarchy.hierarchy,
								);

								setIsMutating(true);
								try {
									await updateHierarchy({
										companyDomainId: companyDomain.id,
										hierarchy: convertHierarchyToUpdateRequest(newHierarchy),
									}).unwrap();
									setHighlightedNodeId(legalEntity.id);
									closeModal();
								} finally {
									setIsMutating(false);
								}
							}
						}}
						onCancel={closeModal}
					/>
					<RemoveCompanyFromHierarchyModal
						isOpen={modalState?.variant === "REMOVING"}
						legalEntity={modalState?.legalEntity}
						onConfirm={async () => {
							if (modalState) {
								await removeFromHierarchy(modalState.legalEntity);
								closeModal();
							}
						}}
						onCancel={closeModal}
					/>
					<MoveCompanyToParentModal
						isOpen={modalState?.variant === "MOVING"}
						legalEntity={modalState?.legalEntity}
						availableParents={
							modalState && hierarchy.hierarchy
								? (() => {
										const currentParentId = findParentOfChild(
											modalState.legalEntity.id,
											hierarchy.hierarchy,
										)?.legalEntity.id;
										return flattenLegalEntitiesInHierarchy(
											hierarchy.hierarchy,
										).filter(
											(candidate) =>
												hierarchy.hierarchy != null &&
												candidate.id !== currentParentId &&
												canMoveWithinHierarchy(
													modalState.legalEntity.id,
													candidate.id,
													hierarchy.hierarchy,
												),
										);
									})()
								: []
						}
						onMove={async (parentId) => {
							if (modalState) {
								const moved = await moveIntoHierarchy(
									modalState.legalEntity.id,
									parentId,
								);
								if (moved) {
									closeModal();
								}
							}
						}}
						onCancel={closeModal}
					/>
					{isEditingStructure ? (
						<DndProvider backend={HTML5Backend}>
							<div className="flex flex-col gap-4 p-4 md:flex-row">
								<div className="min-w-0 flex-1">
									<HierarchyTable
										title={t("Legal entity")}
										expandedByDefault
										standalone={false}
										isDraggable={!isMutating}
										canBeDropped={canBeDropped}
										onDrop={onDropIntoHierarchy}
										onReorder={onReorderWithinHierarchy}
										highlightedNodeId={highlightedNodeId}
										hierarchy={convertCompanyHierarchyToHierarchy(
											hierarchy.hierarchy,
											companyDomain.id,
											permittedLegalEntityIds,
											false,
										)}
									/>
								</div>
								<div className="flex w-full shrink-0 flex-col rounded border border-grey-200 md:w-72">
									<div className="border-grey-200 border-b bg-grey-50 p-3 font-medium text-sm">
										{t("Unsorted companies")}
									</div>
									<p className="border-grey-200 border-b p-3 text-grey-500 text-xs">
										{t(
											"Drag a company onto a row to place it in the structure",
										)}
									</p>
									<DraggableCompanyList
										companies={availableLegalEntities}
										emptyLabel={t("All companies are placed in the structure")}
										dropHintLabel={t("Drop here to remove from the structure")}
										onDropCompany={onDropToUnsorted}
										canDropCompany={canDropToUnsorted}
									/>
								</div>
							</div>
						</DndProvider>
					) : (
						<HierarchyTable
							title={t("Legal entity")}
							expandedByDefault
							hierarchy={convertCompanyHierarchyToHierarchy(
								hierarchy.hierarchy,
								companyDomain.id,
								permittedLegalEntityIds,
								true,
							)}
							renderActions={renderRowActions}
							highlightedNodeId={highlightedNodeId}
						/>
					)}
				</>
			) : !hasMultipleCompanies ? (
				<div className="p-4 text-grey-500 text-sm">
					{t(
						"Group structure only applies once a domain has more than one company",
					)}
				</div>
			) : hasCompanyAdmin ? (
				<div className="p-4">
					<Label id={parentCompanyLabelId}>{t("Parent company")}</Label>
					<Select
						aria-labelledby={parentCompanyLabelId}
						disabled={isMutating}
						options={legalEntities.map((legalEntity) => ({
							label: legalEntity.name,
							value: legalEntity.id,
						}))}
						value={null}
						placeholder={t("Select parent company")}
						onChange={async (legalEntityId) => {
							if (!legalEntityId) {
								return;
							}
							setIsMutating(true);
							try {
								await updateHierarchy({
									companyDomainId: companyDomain.id,
									hierarchy: {
										legalEntityId,
										children: [],
									},
								}).unwrap();
							} catch {
								showErrorNotification({ message: t("Error") });
							} finally {
								setIsMutating(false);
							}
						}}
					/>
				</div>
			) : (
				<div className="p-4 text-grey-500 text-sm">
					{t("No group structure has been created yet")}
				</div>
			)}
		</Card>
	);
};
